import type { W_EquipmentUtilization_FindBySubCategoryTable_VM } from 'ab-api/web/equipmentUtilization/findBySubCategoryTable';

import { somePredicates } from '@acceligentllc/shared/utils/array';
import { moneyNormalizer } from '@acceligentllc/shared/utils/formatting';

import { buildTable } from 'af-utils/table.utils';

import EquipmentUtilizationLevel from 'ab-enums/equipmentUtilizationLevelNew.enum';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import type { TableQuery, TableSortBy } from 'ab-common/dataStructures/tableQuery';

import type { EquipmentUtilizationTableBaseVM } from './types';

const DEFAULT_EQUIPMENT_GROUP_SORT_BY: TableSortBy[] = [{ id: 'groupId', desc: false }, { id: 'timePeriod', desc: true }];
const DEFAULT_EQUIPMENT_SORT_BY: TableSortBy[] = [{ id: 'name', desc: false }, { id: 'timePeriod', desc: true }];

function _getUtilizationRowsFilterByTextPredicate(text: string): Nullable<ArrayPredicate<EquipmentUtilizationTableBaseVM>> {
	const trimmedText = (text ?? '').trim();
	if (!trimmedText) {
		return null;
	}
	const uppercaseText = trimmedText.toUpperCase();

	const filterFunctions: ((_e: EquipmentUtilizationTableBaseVM) => boolean)[] = [
		(_e: EquipmentUtilizationTableBaseVM) => _e.timePeriod.toUpperCase().includes(uppercaseText),
	];

	return somePredicates(filterFunctions);
}

function _filterUtilizationRowsByText(rows: EquipmentUtilizationTableBaseVM[], text: string): EquipmentUtilizationTableBaseVM[] {
	const predicate = _getUtilizationRowsFilterByTextPredicate(text);
	if (!predicate?.length) {
		return rows;
	}
	return rows.filter(predicate);
}

const CSV_HEADER_EQUIPMENT_GROUP_BASE_KEYS = [
	'Year',
	'Scheduled',
	'Not Scheduled',
	'Not Available',
	'Daily Revenue',
	'Daily Cost',
	'Target Profit/Loss',
];

const CSV_HEADER_EQUIPMENT_KEYS = [
	'Equipment ID',
	'Year',
	'Scheduled',
	'Not Scheduled',
	'Not Available',
	'Daily Revenue',
	'Total Revenue',
	'Daily Cost',
	'Total Cost',
	'Target Profit/Loss',
];

export const toEquipmentCostCSVData = (viewModels: W_EquipmentUtilization_FindBySubCategoryTable_VM['items']): string[][] => {
	const header: string[] = [...CSV_HEADER_EQUIPMENT_KEYS];

	const rows: string[][] = viewModels.map((_entry) => {
		const _row: string[] = [
			`${_entry.name} ${_entry.specification}`,
			_entry.timePeriod.toString(),
			`${_entry.daysAssigned} days`,
			`${_entry.daysAvailable} days`,
			`${_entry.daysUnavailable} days`,
			`${moneyNormalizer(_entry.dailyRevenue)}`,
			`${moneyNormalizer(_entry.totalRevenue)}`,
			`${moneyNormalizer(_entry.dailyCost)}`,
			`${moneyNormalizer(_entry.totalCost)}`,
			`${moneyNormalizer(_entry.targetProfit)}`,
		];
		return _row;
	});

	return [header, ...rows];
};

export const getTable = (
	tableRequest: TableQuery,
	level: EquipmentUtilizationLevel,
	data: Nullable<EquipmentUtilizationTableBaseVM[]>
): Nullable<TableContent<EquipmentUtilizationTableBaseVM>> => {
	if (!data) {
		return null;
	}
	const defaultSortBy = level === EquipmentUtilizationLevel.EQUIPMENT ? DEFAULT_EQUIPMENT_SORT_BY : DEFAULT_EQUIPMENT_GROUP_SORT_BY;
	const sortBy: TableSortBy[] = !tableRequest.sortBy?.length ? defaultSortBy : tableRequest.sortBy;
	return buildTable({ ...tableRequest, sortBy }, data, _filterUtilizationRowsByText);
};

export const toCSVData = (viewModels: EquipmentUtilizationTableBaseVM[], firstHeaderKey: string): string[][] => {
	const header: string[] = [firstHeaderKey, ...CSV_HEADER_EQUIPMENT_GROUP_BASE_KEYS];

	const rows: string[][] = viewModels.map((_entry) => {
		const _row: string[] = [
			_entry.name,
			_entry.timePeriod.toString(),
			`${_entry.daysAssigned} days`,
			`${_entry.daysAvailable} days`,
			`${_entry.daysUnavailable} days`,
			`${moneyNormalizer(_entry.dailyRevenue)}`,
			`${moneyNormalizer(_entry.dailyCost)}`,
			`${moneyNormalizer(_entry.targetProfit)}`,
		];
		return _row;
	});

	return [header, ...rows];
};
